import type { BaseSelection } from './base';

export const REMOTE_TEXT_SELECTION_GRACE_MS = 2000;

export type RemoteTextSelectionCache = Map<
  number,
  { selection: BaseSelection; updatedAt: number }
>;

export function stabilizeRemoteTextSelections({
  cache,
  now,
  selections,
  transientGraceMs = REMOTE_TEXT_SELECTION_GRACE_MS,
}: {
  cache: RemoteTextSelectionCache;
  now: number;
  selections: Map<number, BaseSelection[]>;
  transientGraceMs?: number;
}): {
  nextExpiryAt: number | null;
  selections: Map<number, BaseSelection[]>;
} {
  const activeClients = new Set(selections.keys());
  for (const clientId of cache.keys()) {
    if (!activeClients.has(clientId)) {
      cache.delete(clientId);
    }
  }

  let nextExpiryAt: number | null = null;
  const stabilized = new Map<number, BaseSelection[]>();

  selections.forEach((clientSelections, clientId) => {
    const currentTextSelection = clientSelections.find(
      selection => selection.type === 'text'
    );
    if (currentTextSelection) {
      cache.set(clientId, {
        selection: currentTextSelection,
        updatedAt: now,
      });
      stabilized.set(clientId, clientSelections);
      return;
    }

    const previous = cache.get(clientId);
    if (!previous) {
      stabilized.set(clientId, clientSelections);
      return;
    }

    const expiryAt = previous.updatedAt + transientGraceMs;
    if (now <= expiryAt) {
      stabilized.set(clientId, [...clientSelections, previous.selection]);
      nextExpiryAt =
        nextExpiryAt === null ? expiryAt : Math.min(nextExpiryAt, expiryAt);
      return;
    }

    cache.delete(clientId);
    stabilized.set(clientId, clientSelections);
  });

  return { nextExpiryAt, selections: stabilized };
}
